/**
 * Cmd/Ctrl+点击图层缩略图/通道 → 选区（DESIGN.md §7.1 补充，参照 Photoshop）。
 *
 * 跟魔棒/套索工具（`interaction/tools/marquee.ts` 的 `applyCombine`）语义
 * 一致，但这里**不能 import 那个文件**——它在交互层，本文件在应用层，
 * 分层规则只允许上层依赖下层（CLAUDE.md 规则 4）。选区合并只是
 * `union`/`subtract`/`intersect`（`engine/selection/ops.ts`）三个纯函数，
 * 在这里原样重新组一次，比反向依赖交互层便宜。
 */
import { intersect, subtract, union, fromChannel } from '../engine/selection/ops';
import { SelectionMask } from '../engine/selection/mask';
import { flattenLayerToImage, compositeDocument } from './composite';
import type { AppStore } from './store';
import type { LayerId } from '../engine/tile/key';

/** Shift 加选 / Alt 减选 / Shift+Alt 交叉——跟 `marquee.ts` 同一套 PS 惯例。 */
export type SelectionLoadMode = 'replace' | 'add' | 'subtract' | 'intersect';

export function selectionLoadModeFromModifiers(modifiers: {
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}): SelectionLoadMode {
  if (modifiers.shiftKey && modifiers.altKey) return 'intersect';
  if (modifiers.altKey) return 'subtract';
  if (modifiers.shiftKey) return 'add';
  return 'replace';
}

function combine(
  current: SelectionMask | null,
  next: SelectionMask,
  mode: SelectionLoadMode,
): SelectionMask {
  if (current === null) {
    return mode === 'subtract' || mode === 'intersect' ? SelectionMask.empty(next.width, next.height) : next;
  }
  switch (mode) {
    case 'replace':
      return next;
    case 'add':
      return union(current, next);
    case 'subtract':
      return subtract(current, next);
    case 'intersect':
      return intersect(current, next);
  }
}

/**
 * 把某一图层自己的 alpha 通道（不参与合成，`flattenLayerToImage` 同款
 * 语义）转成选区——软选区，alpha 值本身就是选中程度。
 * 返回是否真的执行了（无文档时 false）。
 */
export function loadLayerAlphaSelection(
  store: AppStore,
  layerId: LayerId,
  mode: SelectionLoadMode = 'replace',
): boolean {
  const document = store.document;
  if (document === null) return false;
  const image = flattenLayerToImage(store.tiles, layerId, document.size);
  const mask = fromChannel(image.width, image.height, image.pixels, 3, 4);
  store.setSelection(combine(store.selection, mask, mode));
  return true;
}

/**
 * 通道面板用：把**整份文档合成结果**（不是单图层）的某个通道转成选区。
 * `channel` 是 RGBA 里的偏移（0=R，1=G，2=B，3=Alpha）。
 */
export function loadCompositeChannelSelection(
  store: AppStore,
  channel: 0 | 1 | 2 | 3,
  mode: SelectionLoadMode = 'replace',
): boolean {
  const document = store.document;
  if (document === null) return false;
  const composite = compositeDocument(document, store.tiles);
  const mask = fromChannel(composite.width, composite.height, composite.pixels, channel, 4);
  store.setSelection(combine(store.selection, mask, mode));
  return true;
}
